import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { StickyContainer } from 'react-sticky';
import styled from 'styled-components';
import { CpInviteToProjectModal } from 'components/organisms/customer-portal/components/cp-invite-to-project-modal/cp-invite-to-project-modal';
import { CpNavBarRenderer } from 'components/organisms/customer-portal/components/cp-nav-bar';
import { CpNavbarTitle } from 'components/organisms/customer-portal/components/cp-nav-bar-title';
import { CpPageRenderer } from 'components/organisms/customer-portal/components/cp-page-renderer';
import { CpTaskModalWrapper } from 'components/organisms/customer-portal/components/cp-task-modal-wrapper';
import { useCustomerPortalHashMap } from 'components/organisms/customer-portal/utils';
import { CpProjectUpdatePane } from 'components/organisms/customer-portal/widgets/project-updates/cp-project-update-pane';
import { NotificationsConfiguration } from 'components/organisms/notifications';
import {
  useCustomerPortalProjects,
  useGetLivePreview,
} from 'hooks/customer-portal/use-customer-portal-hooks';
import { useAccountDataQuery } from 'queries/account';
import { useProjectDetailQuery } from 'queries/project';
import { SlackLandingPage } from 'routes/settings/integrations/slack/slack-landing-page';
import { CpModalRoot, ThemeVariablesWrapper } from './theme-variables-wrapper';

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  @media (min-height: 700px) {
    &:has([data-page-tyoe='CHAT']) {
      overflow-y: hidden;
    }
  }
`;

export const CpPreviewRoute: React.FC = () => {
  const [isInviteModalVisible, setInviteModalVisiblity] = useState(false);
  const { hashMap, setHashMap } = useCustomerPortalHashMap();

  const { projectId: _projectId } = useParams<{ projectId: string }>();
  const [projectId, setProjectId] = useState(() => {
    if (hashMap?.ppi) {
      return parseInt(hashMap.ppi, 10);
    }

    return parseInt(_projectId, 10);
  });

  const { data, refetch } = useGetLivePreview();
  const { data: allProjects = [] } = useCustomerPortalProjects(projectId);

  const pages = useMemo(() => {
    if (data?.pages) {
      return _.sortBy(
        data?.pages,
        (a) => data?.portalMeta?.pagesOrder?.indexOf(a.pageReferenceId) ?? 0,
      );
    }

    return data?.pages ?? [];
  }, [data]);

  const [currentPageRefId, setPageRefId] = React.useState<string | undefined>(
    data?.currentPage,
  );

  const currentPageData = useMemo(
    () => pages.find((p) => p.pageReferenceId === currentPageRefId),
    [currentPageRefId, pages],
  );

  const { data: accountData } = useAccountDataQuery();
  const { data: projectData } = useProjectDetailQuery(
    ['project_id', projectId ?? -1],
    {
      enabled: projectId !== undefined,
    },
  );

  useEffect(() => {
    if (pages.length > 0 && !currentPageRefId) {
      const homePage = pages.find((p) => p.pageType === 'HOME');

      setPageRefId(homePage?.pageReferenceId ?? pages[0].pageReferenceId);
    }
  }, [currentPageRefId, pages]);

  useEffect(() => {
    window.onmessage = (msg: MessageEvent) => {
      if (msg?.data?.type === 'cp-conditions-update') {
        refetch();
      } else if (msg?.data?.type === 'get-project-id') {
        setProjectId(parseInt(msg.data.projectId, 10));
      }
    };

    return () => {
      window.onmessage = null;
    };
  }, [projectId, refetch]);

  useEffect(() => {
    if (!projectId) {
      window.parent.postMessage({ type: 'get-project-id' }, '*');
    }
  }, [projectId]);

  useEffect(() => {
    if (hashMap?.ppi) {
      setProjectId(parseInt(hashMap.ppi, 10));
    }

    setProjectId(parseInt(_projectId, 10));
  }, [_projectId, hashMap.ppi]);

  useEffect(() => {
    if (data) {
      setPageRefId(data.currentPage);
    }
  }, [data]);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    wrapperRef.current?.scrollTo({ top: 0 });
  }, [currentPageRefId]);

  return (
    <Wrapper className="rl-cp-top-scroll-wrapper" ref={wrapperRef}>
      <ThemeVariablesWrapper themeId={data?.customerPortalThemeId ?? -1}>
        <CpModalRoot />
        <CpTaskModalWrapper />
        <CpProjectUpdatePane />

        <CpNavBarRenderer
          showInviteButton
          onShareClick={() => {
            setInviteModalVisiblity(true);
          }}
          dontShowNotificationSettings
          onNotificationSettingsClick={() => {
            setPageRefId('notifications_settings');
          }}
          onSlackIntegrationClick={() => {
            setPageRefId('slack_page');
          }}
          selectedProject={projectData}
          pages={pages}
          onProjectChange={(pId) => {
            setHashMap('ppi', pId?.toString());
          }}
          projects={allProjects}
          title={
            accountData && projectData ? (
              <CpNavbarTitle
                projectName={projectData.projectName}
                customer={projectData.customer}
                vendor={accountData.account.company}
              />
            ) : (
              <></>
            )
          }
          activePageRefId={currentPageRefId}
          onPageClick={(p) => {
            setPageRefId(p?.pageReferenceId);
          }}
        />
        {currentPageData && projectId ? (
          <CpPageRenderer
            page={currentPageData}
            projectId={projectId}
            onSectionClick={() => {}}
            selectedSection={undefined}
            showFooter
          />
        ) : (
          <></>
        )}
        {accountData?.accountMeta?.features
          ?.disableCustomerSlackConnect ? null : currentPageRefId ===
          'slack_page' ? (
          <SlackLandingPage />
        ) : (
          <></>
        )}
        {currentPageRefId === 'notifications_settings' ? (
          <StickyContainer>
            <NotificationsConfiguration />
          </StickyContainer>
        ) : (
          <></>
        )}
        <CpInviteToProjectModal
          projectId={projectId}
          visible={isInviteModalVisible}
          onClose={() => setInviteModalVisiblity(false)}
          isPreviewModeOn
        />
      </ThemeVariablesWrapper>
    </Wrapper>
  );
};
